// Formateadores de la ficha de producto.
//
// La plantilla de la ficha es el HTML de resources/views/front/products/details.blade.php
// (docs/frontend/product-page.md): cada importe que Vue pinta con `v-text`
// tiene al lado la replica SSR que imprime Blade. Estas funciones son el
// espejo EXACTO de esas replicas — mismo texto, byte a byte, para el mismo
// valor — para que el primer render de Vue no cambie ni un caracter de lo
// que ya se ve.
//
//   formatCurrency   <->  number_format($v, 2, ',', '.') . ' €'        (details.blade.php:734, 746, 751, 764, 772)
//   formatPercentage <->  $discountLabel                              (details.blade.php:40-43)
//   formatEsNumber   <->  $formatNumber / $fmtEs                      (ProductDetailsController.php:360-379, details.blade.php:46)
//
// Antes de esto la ficha usaba `Intl.NumberFormat`, que en `de-DE` (estilo
// currency) separa el importe del € con un espacio duro (U+00A0) y no con el
// espacio normal que escribe Blade: la divergencia del par 18 de
// docs/modernization/01-audit.md § F1.8.
//
// No se reutiliza `resources/js/services/moneyFormatter.js` (el del checkout)
// porque aquel no tiene guarda —devuelve "NaN"— y redondea con `toFixed`,
// mientras que aqui hace falta lo que hace PHP: valores no numericos valen 0
// y el redondeo es el de `round()` de PHP.

const DEFAULT_DECIMALS = 2;

// Espejo del cast `(float)` de PHP: null, "", false y el texto no numerico
// valen 0; una cadena numerica se lee por su prefijo ("12abc" -> 12).
function toFloat(value) {
    const number = typeof value === "number" ? value : parseFloat(value);

    return isFinite(number) ? number : 0;
}

// Espejo de `round()` de PHP (8.2), que antes de redondear "pre-redondea" el
// valor escalado a 15 digitos significativos: por eso PHP da 1,01 para 1.005
// mientras que (1.005).toFixed(2) da "1.00". Devuelve los digitos del valor
// absoluto ya redondeado (los "centimos"), para construir el texto sin volver
// a pasar por un float.
function absoluteDigits(number, decimals) {
    const scaled = number * Math.pow(10, decimals);

    // Fuera de ese rango PHP deja el valor como esta (math.c corta en 1e15).
    // Ningun precio llega aqui; se define para no romper.
    if (!isFinite(scaled) || Math.abs(scaled) >= 1e15) {
        return Math.abs(number).toFixed(decimals).replace(".", "");
    }

    return String(Math.round(Math.abs(Number(scaled.toPrecision(15)))));
}

// Espejo de number_format($value, $decimals, $decPoint, $thousandsSep).
export function numberFormat(
    value,
    decimals = DEFAULT_DECIMALS,
    decPoint = ",",
    thousandsSep = "."
) {
    const number = toFloat(value);

    let digits = absoluteDigits(number, decimals);
    if (digits.length <= decimals) {
        digits = digits.padStart(decimals + 1, "0");
    }

    let integerPart = decimals > 0 ? digits.slice(0, -decimals) : digits;
    const decimalPart = decimals > 0 ? digits.slice(-decimals) : "";

    if (thousandsSep) {
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
    }

    // PHP no imprime "-0,00": el signo sale del valor ya redondeado.
    const sign = number < 0 && Number(digits) > 0 ? "-" : "";

    return sign + integerPart + (decimals > 0 ? decPoint + decimalPart : "");
}

// number_format($v, 2, ',', '.') . ' €' — espacio normal, no U+00A0.
export function formatCurrency(value) {
    return numberFormat(value, DEFAULT_DECIMALS, ",", ".") + " €";
}

// $discountLabel: entero -> "5%", decimal -> "5.50%" (punto decimal, sin
// separador de miles, tal y como lo escribe la replica SSR).
export function formatPercentage(value) {
    const number = toFloat(value);
    const integer = Math.trunc(number);

    if (number === integer) {
        return String(integer) + "%";
    }

    return numberFormat(number, DEFAULT_DECIMALS, ".", "") + "%";
}

// $formatNumber del controlador (y su gemelo $fmtEs): limpia el numero cuando
// llega ya formateado ("1.234,56"), devuelve "" si no hay valor, y agrupa
// miles solo a partir de 10000 — que es lo que hace Intl con `es-ES`
// (minimumGroupingDigits = 2).
export function formatEsNumber(value) {
    if (value === null || value === undefined || value === "") {
        return "";
    }

    let str = String(value).trim();
    if (/\d+\.\d+,\d+/.test(str)) {
        str = str.replace(/\./g, "").replace(",", ".");
    } else if (str.includes(",") && !str.includes(".")) {
        str = str.replace(",", ".");
    } else if (str.includes(".") && (str.match(/\./g) || []).length > 1) {
        str = str.replace(/\./g, "");
    }

    const number = Number(str);
    if (!isFinite(number)) {
        return "";
    }

    return numberFormat(number, DEFAULT_DECIMALS, ",", number >= 10000 ? "." : "");
}

export default { numberFormat, formatCurrency, formatPercentage, formatEsNumber };
